import {
  readRentalOperatingHours,
  rentalStartOptions,
  wallClockTimeExists,
} from "@obby/lib";

/**
 * The opening hours that apply to ONE rental ticket on a listing.
 *
 * Takes the component's own inputs — the course, the selected ticket, the
 * server's rental-ticket universe — rather than a pre-assembled filter, so
 * there is no second place to assemble it wrongly. The filter is only ever
 * complete or absent, never half-built: `selectSlotsForRentalTicket` silently
 * defers when given a ticket id without the universe, so a half-built filter
 * looks like narrowing while doing nothing, and no test of the library alone
 * would catch it.
 *
 * `rentalTicketIds` MUST come from the server. The widget's own ticket list is
 * filtered per buyer, so a universe derived from it would narrow differently
 * depending on who is looking. Undefined (an older API) defers to the full
 * slot list, which is the pre-filter behaviour.
 */
export function rentalOperatingHoursForTicket(params: {
  /**
   * Only `appointmentScheduleSlots` is read. Its element type is derived from
   * the consumer rather than restated: @obby/lib keeps `ScheduleSlot` private,
   * and `unknown[]` does not satisfy it.
   */
  course:
    | { appointmentScheduleSlots?: Parameters<typeof readRentalOperatingHours>[0] }
    | null
    | undefined;
  ticket: { id: string } | null | undefined;
  rentalTicketIds: string[] | undefined;
}) {
  const { course, ticket, rentalTicketIds } = params;
  return readRentalOperatingHours(course?.appointmentScheduleSlots, {
    ticketId: ticket?.id,
    rentalTicketIds,
  });
}

/**
 * The calendar day the customer clicked, as "YYYY-MM-DD".
 *
 * Read off the LOCAL calendar fields deliberately. `selectedDate` comes from
 * the Calendar as midnight in the BROWSER's zone, so handing the instant itself
 * to `wallClockInstant` — which resolves start-of-day in the LISTING's zone —
 * moves the booking a day backwards for any customer browsing from a zone ahead
 * of the listing's: 8 Aug 00:00 in Sydney is 7 Aug 15:00 in London.
 * "YYYY-MM-DD" has no such ambiguity; moment parses it as midnight in the
 * target zone, which is the day the customer clicked.
 */
export function rentalDayString(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(date.getDate()).padStart(2, "0")}`;
}

/**
 * The start times a rental listing can actually be booked at on one day.
 *
 * `rentalStartOptions` is pure minutes-from-midnight arithmetic and knows
 * nothing about DST, so it happily offers times that do not exist. This drops
 * them, which `wallClockInstant`'s own contract requires of anything building a
 * LIST of options (`startOptions.ts:191-193`).
 *
 * ⚠ Without the filter, on the spring-forward Sunday a listing open across
 * 01:00–01:59 rendered both "01:30" and "02:30", and `wallClockInstant`
 * resolved the first FORWARD onto the second. A customer tapped 01:30, was
 * charged, and was emailed a 02:30 hire — and because both entries resolved to
 * one instant, those pool units sold twice. Dev resolved the gap backward
 * instead, which the server's opening-hours check refused visibly, so leaving
 * this unfiltered turned a fail-loud case into a silently wrong booking.
 *
 * Extracted from the component so the day string is derived in ONE place: the
 * grid and `buildItem` must ask about the same day, or the filter hides one
 * day's gap while the booking is built against another's.
 */
export function rentalStartOptionsForDay(params: {
  windows: any;
  durationMinutes: number;
  fractionMinutes: number;
  date: Date;
  timezone: string;
}): number[] {
  const { windows, durationMinutes, fractionMinutes, date, timezone } = params;
  const dayStr = rentalDayString(date);

  return rentalStartOptions({
    windows,
    durationMinutes,
    fractionMinutes,
  }).filter((minutesOfDay: number) =>
    wallClockTimeExists({ date: dayStr, timezone, minutesOfDay }),
  );
}
